import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf, fdateShort } from "@/lib/format";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Sparkles } from "lucide-react";

const PERKS = [
  "La Gazette des Mariés (journal personnalisé + export PDF)",
  "Site de mariage complet & publication",
  "Export HD des faire-part (PDF & PNG)",
  "Plan de table et invités sans limite",
  "Assistance prioritaire",
];

export default function Premium() {
  const { wedding, premium, refreshWedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.filter((p) => (p.amount || 0) > 0)));
  }, []);

  const subscribe = async (code) => {
    // Paid plans ALWAYS go through Stripe Checkout. The frontend never activates Premium.
    setBusy(code);
    try {
      const { data } = await api.post(`/payments/checkout`, { wedding_id: wid, plan_code: code, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Paiement indisponible pour le moment.");
      setBusy("");
    }
  };

  if (!plans) return <Loader />;
  const active = premium?.active;

  return (
    <div>
      <PageHeader eyebrow="Abonnement" title="Premium"
        description="Débloquez tous les outils créatifs pour préparer un mariage inoubliable." />

      {active ? (
        <div className="card-lux p-6 mb-8 flex items-center gap-4" data-testid="premium-active-card"
          style={{ background: "linear-gradient(120deg,#1c1b1a,#3a332a)" }}>
          <div className="w-12 h-12 rounded-2xl bg-champagne/20 flex items-center justify-center"><Crown className="w-6 h-6 text-champagne" /></div>
          <div className="text-white">
            <div className="font-serif text-xl">Premium actif</div>
            <div className="text-sm text-white/70">
              {premium.plan_name}{premium.expires_at ? ` · renouvellement le ${fdateShort(premium.expires_at)}` : ""}
            </div>
          </div>
        </div>
      ) : premium?.status === "expired" ? (
        <div className="card-lux p-5 mb-8 border-amber-200 bg-amber-50/60" data-testid="premium-expired-card">
          <div className="font-medium text-stone-800">Votre abonnement Premium a expiré</div>
          <div className="text-sm text-stone-600 mt-1">Vos données sont conservées intégralement. Réactivez Premium pour reprendre là où vous vous étiez arrêté — rien n'a été supprimé.</div>
        </div>
      ) : null}

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="grid sm:grid-cols-2 gap-4">
          {plans.map((p) => (
            <div key={p.code} className={`card-lux p-6 flex flex-col ${p.recommended ? "ring-2 ring-champagne" : ""}`} data-testid={`plan-${p.code}`}>
              {p.recommended && <div className="eyebrow text-champagne mb-1">Recommandé</div>}
              <div className="font-serif text-xl">{p.name}</div>
              <div className="font-mono text-3xl mt-2">{chf(p.amount)}<span className="text-sm text-stone-400 font-sans">/{p.billing_type === "yearly" ? "an" : p.billing_type === "one_time" ? "unique" : "mois"}</span></div>
              <Button className="rounded-full mt-5" disabled={busy === p.code || active} onClick={() => subscribe(p.code)} data-testid={`subscribe-${p.code}`}>
                {active ? "Déjà Premium" : busy === p.code ? "…" : (premium?.status === "expired" ? "Réactiver" : "Choisir")}
              </Button>
            </div>
          ))}
        </div>
        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-champagne" /><div className="font-serif text-lg">Inclus dans Premium</div></div>
          <ul className="space-y-2.5">
            {PERKS.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-700"><Check className="w-4 h-4 text-sage mt-0.5 shrink-0" /> {b}</li>
            ))}
          </ul>
          <p className="text-xs text-stone-400 mt-5">À l'expiration, vos données restent enregistrées : vous ne pouvez simplement plus modifier les fonctions Premium jusqu'à réactivation.</p>
        </div>
      </div>
    </div>
  );
}
